import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { ImageUploader } from "@/components/ImageUploader";
import { VideoUploader } from "@/components/VideoUploader";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";
import { US_STATES, PROPERTY_TYPES } from "@/lib/listings";
import { ArrowRight, Save } from "lucide-react";
import { toast } from "sonner";

export default function PostProperty() {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    property_type: "apartment",
    state: "",
    city: "",
    zip_code: "",
    price: "",
    bedrooms: "",
    bathrooms: "",
    furnished: false,
    internet_included: false,
    utilities_included: false,
    description: "",
    owner_name: user?.name || "",
    phone: user?.phone || "",
    images: [],
    videos: [],
    accept_terms: false,
  });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, owner_name: f.owner_name || user.name || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.state || !form.city || !form.zip_code || !form.price || !form.description || !form.owner_name || !form.phone) {
      toast.error(t("يرجى تعبئة كل الحقول المطلوبة", "Please fill required fields"));
      return;
    }
    if (!form.accept_terms) {
      toast.error(t("يجب الموافقة على الشروط والأحكام قبل نشر الإعلان.", "You must accept the Terms and Conditions before publishing."));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : 0,
        bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : 0,
        accept_terms: true,
      };
      const createResp = await axios.post(`${API}/properties`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // After creation, register each uploaded image with the moderation pipeline.
      // We have to do this here (not inside ImageUploader) because the property_id
      // doesn't exist until the listing is created.
      const newId = createResp.data.property_id;
      if (newId && (form.images || []).length > 0) {
        await Promise.all(form.images.map((path) =>
          axios.post(`${API}/media`, {
            listing_type: "property", listing_id: newId, url: path, media_type: "image",
          }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
        ));
      }
      if (newId && (form.videos || []).length > 0) {
        await Promise.all(form.videos.map((path) =>
          axios.post(`${API}/media`, {
            listing_type: "property", listing_id: newId, url: path, media_type: "video",
          }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
        ));
      }
      toast.success(t("تم إرسال الإعلان للمراجعة", "Listing submitted for review"));
      navigate("/housing/mine");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message_ar || detail?.message_en || t("فشل النشر", "Failed to post"));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("نشر إعلان عقار", "Post a Property")}>
      <form onSubmit={submit} className="max-w-4xl space-y-6" data-testid="post-property-form">
        <Link to="/housing" className="inline-flex items-center gap-2 text-yellow-500 hover:underline">
          <ArrowRight size={16} /> {t("رجوع للسوق", "Back to marketplace")}
        </Link>

        {/* Basics */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("معلومات أساسية", "Basics")}</h3>
          <input className={inputCls} placeholder={t("عنوان الإعلان", "Listing title")}
            value={form.title} onChange={(e) => update("title", e.target.value)} required data-testid="prop-title" />
          <div className="grid md:grid-cols-2 gap-4">
            <select className={inputCls} value={form.property_type} onChange={(e) => update("property_type", e.target.value)} data-testid="prop-type">
              {PROPERTY_TYPES.map((p) => <option key={p.value} value={p.value}>{language === "ar" ? p.label_ar : p.label_en}</option>)}
            </select>
            <input className={inputCls} type="number" placeholder={t("السعر بالدولار شهرياً", "Monthly price ($)")}
              value={form.price} onChange={(e) => update("price", e.target.value)} required data-testid="prop-price" />
          </div>
          {form.price && (
            <p className="text-xs text-slate-400 mt-1" data-testid="prop-price-hint">
              {t(
                `سعرك: $${Number(form.price).toLocaleString()} — سيظهر للعميل: $${(Number(form.price) * 1.05).toLocaleString(undefined, {maximumFractionDigits: 2})} (شامل 5% رسوم منصة عرب سند).`,
                `Your price: $${Number(form.price).toLocaleString()} — Customer sees: $${(Number(form.price) * 1.05).toLocaleString(undefined, {maximumFractionDigits: 2})} (includes 5% Arab Sanad platform fee).`
              )}
            </p>
          )}
        </div>

        {/* Location */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("الموقع", "Location")}</h3>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t("ابحث في خرائط Google لملء الحقول تلقائياً", "Search Google to auto-fill")}</label>
            <PlacesAutocompleteInput
              placeholder={t("ابحث عن العنوان...", "Search address...")}
              types="address"
              dataTestId="prop-place-search"
              onSelect={(d) => {
                if (d.state) update("state", d.state);
                if (d.city) update("city", d.city);
                if (d.zip_code) update("zip_code", d.zip_code);
              }}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <select className={inputCls} value={form.state} onChange={(e) => update("state", e.target.value)} required data-testid="prop-state">
              <option value="">{t("اختر الولاية", "Select State")}</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
            </select>
            <input className={inputCls} placeholder={t("المدينة", "City")} value={form.city} onChange={(e) => update("city", e.target.value)} required data-testid="prop-city" />
            <input className={inputCls} placeholder={t("الرمز البريدي", "ZIP code")} value={form.zip_code} onChange={(e) => update("zip_code", e.target.value)} required data-testid="prop-zip" />
          </div>
        </div>

        {/* Details */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("تفاصيل العقار", "Details")}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input className={inputCls} type="number" placeholder={t("عدد غرف النوم", "Bedrooms")} value={form.bedrooms} onChange={(e) => update("bedrooms", e.target.value)} data-testid="prop-bedrooms" />
            <input className={inputCls} type="number" step="0.5" placeholder={t("عدد الحمامات", "Bathrooms")} value={form.bathrooms} onChange={(e) => update("bathrooms", e.target.value)} data-testid="prop-bathrooms" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { key: "furnished", ar: "مفروش", en: "Furnished" },
              { key: "internet_included", ar: "إنترنت مشمول", en: "Internet included" },
              { key: "utilities_included", ar: "فواتير مشمولة", en: "Utilities included" }
            ].map((opt) => (
              <label key={opt.key} className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 cursor-pointer">
                <input type="checkbox" checked={form[opt.key]} onChange={(e) => update(opt.key, e.target.checked)} data-testid={`prop-${opt.key}`} />
                <span className="text-white text-sm">{language === "ar" ? opt.ar : opt.en}</span>
              </label>
            ))}
          </div>
          <textarea className={inputCls} rows={5} placeholder={t("وصف تفصيلي للعقار", "Detailed description")} value={form.description} onChange={(e) => update("description", e.target.value)} required data-testid="prop-description" />
        </div>

        {/* Images */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("الصور", "Images")}</h3>
          <ImageUploader value={form.images} onChange={(imgs) => update("images", imgs)} />
        </div>

        {/* Videos — unlimited, admin-reviewed before going live */}
        <div className="glass-card rounded-2xl p-5 space-y-3">
          <VideoUploader value={form.videos} onChange={(vids) => update("videos", vids)} />
        </div>

        {/* Contact */}
        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("معلومات التواصل", "Contact")}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input className={inputCls} placeholder={t("اسم المالك", "Owner name")} value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)} required data-testid="prop-owner-name" />
            <input className={inputCls} placeholder={t("رقم الهاتف", "Phone")} value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="prop-phone" />
          </div>
        </div>

        {/* Terms & Conditions — required before publishing */}
        <div className="glass-card rounded-2xl p-5 border border-yellow-500/30 bg-yellow-500/5" data-testid="terms-section">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.accept_terms}
              onChange={(e) => update("accept_terms", e.target.checked)}
              className="mt-1 w-5 h-5 accent-yellow-500"
              data-testid="prop-accept-terms"
            />
            <span className="text-sm text-slate-200 leading-relaxed">
              {t(
                "أوافق على شروط وأحكام عرب سند. أُقرّ بأن السعر المعروض على العميل سيشمل نسبة منصة عرب سند، وأن المنصة تحتفظ بحقها في مراجعة وقبول أو رفض الإعلان قبل نشره.",
                "I accept Arab Sanad's Terms and Conditions. I acknowledge that the price shown to customers will include Arab Sanad's platform fee, and that Arab Sanad reserves the right to review, accept, or reject the listing before publishing."
              )}
            </span>
          </label>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={submitting || !form.accept_terms} className="btn-primary" data-testid="submit-property-btn">
            <Save className="ml-2" size={18} />
            {submitting ? t("جاري النشر...", "Posting...") : t("نشر الإعلان", "Post Listing")}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
